import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, Trash, Pencil } from '@phosphor-icons/react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { usePagination } from '../components/TablePagination';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const empty = () => ({ id: '', name: '', percentage: 0 });

export const TaxesPage = () => {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState(empty());
  const [search, setSearch] = useState('');

  const load = async () => {
    try { setList((await axios.get(`${API_URL}/api/taxes`, { withCredentials: true })).data); }
    catch (e) { console.error(e); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form, percentage: parseFloat(form.percentage) || 0 };
    if (edit) await axios.put(`${API_URL}/api/taxes/${form.id}`, payload, { withCredentials: true });
    else await axios.post(`${API_URL}/api/taxes`, payload, { withCredentials: true });
    setOpen(false); setForm(empty()); setEdit(false); load();
  };
  const onEdit = (t) => { setForm({ ...empty(), ...t }); setEdit(true); setOpen(true); };
  const onDel = async (id) => {
    if (!window.confirm('Delete this tax?')) return;
    await axios.delete(`${API_URL}/api/taxes/${id}`, { withCredentials: true });
    load();
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? list.filter((t) => `${t.name} ${t.percentage}`.toLowerCase().includes(q)) : list;
  }, [list, search]);
  const { paged, controls, startIdx } = usePagination(filtered, { storageKey: 'taxes-page-size', testIdPrefix: 'tax' });

  return (
    <div data-testid="taxes-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--dx-text)' }}>Taxes</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--dx-text2)' }}>Tax rates (GST 18%, IGST 5%, etc.)</p>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setForm(empty()); setEdit(false); } }}>
          <DialogTrigger asChild>
            <Button data-testid="add-tax-btn" className="text-white" style={{ background: 'var(--dx-primary)' }}>
              <Plus size={18} className="mr-1" /> Add Tax
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{edit ? 'Edit Tax' : 'Add Tax'}</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div><Label>Tax Name *</Label><Input required placeholder="GST 18%" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="tax-name" /></div>
              <div><Label>Percentage *</Label><Input required type="number" step="0.01" value={form.percentage} onChange={(e) => setForm({ ...form, percentage: e.target.value })} data-testid="tax-pct" /></div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button type="submit" className="text-white" style={{ background: 'var(--dx-primary)' }} data-testid="save-tax-btn">{edit ? 'Update' : 'Save'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="dx-card">
        <div className="p-3 border-b" style={{ borderColor: 'var(--dx-border)' }}>
          <Input placeholder="Search tax name…" value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-md" data-testid="tax-search" />
        </div>
        <Table>
          <TableHeader><TableRow style={{ background: 'var(--dx-bg)' }}>
            <TableHead>#</TableHead><TableHead>NAME</TableHead><TableHead>PERCENTAGE</TableHead><TableHead className="text-right">ACTIONS</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {paged.length === 0 ? (
              <TableRow><TableCell colSpan={4} className="text-center py-8" style={{ color: 'var(--dx-text2)' }}>{list.length === 0 ? 'No tax entries.' : 'No matches.'}</TableCell></TableRow>
            ) : paged.map((t, i) => (
              <TableRow key={t.id} data-testid={`tax-row-${t.id}`}>
                <TableCell className="text-gray-400">{startIdx + i + 1}</TableCell>
                <TableCell className="font-medium">{t.name}</TableCell>
                <TableCell className="font-semibold">{t.percentage}%</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button size="sm" variant="outline" onClick={() => onEdit(t)}><Pencil size={14} /></Button>
                    <Button size="sm" variant="outline" className="text-red-600" onClick={() => onDel(t.id)}><Trash size={14} /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {controls}
      </Card>
    </div>
  );
};

export default TaxesPage;
